<script setup lang="ts">
import { toHead } from 'vue-datocms'
import { queryWork } from '~/graphql/work'
import { useSchemaGraph, buildSchemaWebPage } from '~/composables/schema'
import { schemaIds } from '~/types/schema'
import { ModelApiKey } from '~/types/models'

const route = useRoute()

const { throwError } = useNuxtError()

const { data, error } = await useDatoQuery(queryWork, {
  variables: {
    slug: route.params.slug as string
  }
})

if (!data.value?.work || error.value) {
  console.error(error.value)
  throwError(404)
}

const appConfig = useAppConfig()
const isFullScreenVideoVisible = ref(false)

if (import.meta.env.SSR) {
  useTheme(AppTheme.LIGHT)
}

onMounted(() => {
  if (import.meta.client) {
    useTheme(AppTheme.LIGHT)
  }
})

const { data: pager } = await useCollectionPager(ModelApiKey.Work, route.params.slug as string)

useHead(() => {
  return toHead(data.value?.work?._seoMetaTags || [])
})

const config = useRuntimeConfig()
const { usePageSchema } = useSchemaGraph()
const ids = schemaIds(config.public.appUrl)
const base = config.public.appUrl.replace(/\/$/, '')

usePageSchema([
  buildSchemaWebPage({
    id: `${base}${route.path}#webpage`,
    url: `${base}${route.path}`,
    name: data.value?.work?.title ?? undefined,
    description: data.value?.work?.overview ?? undefined,
    inLanguage: 'en',
    isPartOf: { '@id': ids.WEBSITE }
  })
])
</script>

<template>
  <div v-if="data?.work">
    <ClientOnly v-if="appConfig.ui.enableFluidThree">
      <FluidWhite />
    </ClientOnly>
    <article class="relative mb-12 lg:mb-48">
      <header class="mb-8 lg:mb-18">
        <div class="relative mb-8 w-full lg:mb-18 lg:h-screen lg:bg-black lg:text-white">
          <div class="relative aspect-square w-full bg-black lg:aspect-video lg:h-full">
            <VideoLoop
              v-if="data.work.videoLoop"
              class="h-full w-full object-cover"
              :poster="data.work.videoLoop.video.thumbnailUrl"
              :sources="[
                { type: data.work.videoLoop.mimeType, src: data.work.videoLoop.video.mp4Url }
              ]"
              :width="data.work.videoLoop.width ?? 1920"
              :height="data.work.videoLoop.height ?? 1080"
            />
            <Image
              v-else-if="data.work.image"
              class="h-full w-full object-cover"
              :src="
                data.work.image.responsiveImage
                  ? data.work.image.responsiveImage?.src
                  : data.work.image.url
              "
              :srcset="
                data.work.image.responsiveImage
                  ? data.work.image.responsiveImage?.webpSrcSet
                  : undefined
              "
              :width="
                data.work.image.responsiveImage
                  ? data.work.image.responsiveImage?.width
                  : (data.work.image.width ?? 1280)
              "
              :height="
                data.work.image.responsiveImage
                  ? data.work.image.responsiveImage?.height
                  : (data.work.image.height ?? 853)
              "
              :alt="data.work.image.alt ?? ''"
              sizes="100vw"
            />
            <div
              class="pointer-events-none absolute left-0 top-0 h-full w-full bg-detail-overlay"
            ></div>
            <ButtonGlass
              v-if="data.work.video"
              class="!absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 lg:hidden"
              size="sm"
              @click="isFullScreenVideoVisible = true"
            >
              PLAY
            </ButtonGlass>
          </div>
          <div
            class="z-[1] mt-8 flex max-w-editor flex-col justify-end gap-2 px-4 lg:absolute lg:bottom-8 lg:left-10 lg:mt-0 lg:gap-8 lg:px-0 lg:text-base"
          >
            <div class="text-sm font-semibold uppercase">WORK</div>
            <Heading v-if="data.work.title" :size="4"
              ><TextReveal>{{ data.work.title }}</TextReveal></Heading
            >
          </div>
          <ScrollDown align="right" :hide-on-mobile="true" color="white" />
          <ButtonGlass
            v-if="data.work.video"
            class="!absolute left-1/2 top-1/2 z-[1] hidden -translate-x-1/2 -translate-y-1/2 lg:block"
            @click="isFullScreenVideoVisible = true"
          >
            PLAY
          </ButtonGlass>
          <Transition name="videoOverlay">
            <VideoFullScreen
              v-if="data.work.video && isFullScreenVideoVisible"
              :options="{
                controls: true,
                autoplay: true,
                preload: 'metadata',
                sources: [
                  {
                    src: data.work?.video?.video?.streamingUrl ?? '',
                    type: 'application/x-mpegURL'
                  }
                ],
                fill: true,
                bigPlayButton: false,
                controlBar: {
                  volumePanel: false,
                  playToggle: false,
                  fullscreenToggle: false,
                  pictureInPictureToggle: false,
                  currentTimeDisplay: false,
                  remainingTimeDisplay: false
                }
              }"
              @on-close="isFullScreenVideoVisible = false"
            />
          </Transition>
        </div>
      </header>
      <div class="relative lg:min-h-[16rem]">
        <div class="mx-auto mb-8 max-w-editor px-4 lg:px-10">
          <div
            class="mx-auto flex w-full flex-col gap-4 text-sm md:flex-row xl:absolute xl:left-10 xl:top-0 xl:ml-0 xl:max-w-[11.5rem] xl:flex-col xl:gap-8 2xl:max-w-xs"
          >
            <div v-if="data.work.clients.length" class="flex flex-col gap-1 md:flex-1 xl:grow-0">
              <div><TextReveal>CLIENT</TextReveal></div>
              <div>
                <TextReveal>
                  <span v-for="(item, idx) in data.work.clients" :key="idx"
                    >{{ item.name
                    }}<span v-if="idx < (data.work.clients.length ?? 0) - 1">,&nbsp;</span></span
                  >
                </TextReveal>
              </div>
            </div>
            <div v-if="data.work.category.length" class="flex flex-col gap-1 md:flex-1 xl:grow-0">
              <div><TextReveal>PROJECT TYPE</TextReveal></div>
              <nav aria-label="Category">
                <TextReveal>
                  <TagLink
                    v-for="category in data.work.category"
                    :key="category.id"
                    :data="category"
                  />
                </TextReveal>
              </nav>
            </div>
            <div v-if="data.work.services.length" class="flex flex-col gap-1 md:flex-1 xl:grow-0">
              <div><TextReveal>SERVICES</TextReveal></div>
              <div>
                <TextReveal>
                  <span v-for="(item, idx) in data.work.services" :key="idx"
                    >{{ item.title
                    }}<span v-if="idx < (data.work.services.length ?? 0) - 1">,&nbsp;</span></span
                  >
                </TextReveal>
              </div>
            </div>
          </div>
        </div>

        <div v-if="data.work.overview" class="mx-auto mb-6 max-w-editor px-4 lg:mb-36 lg:px-10">
          <div>OVERVIEW</div>
          <div class="text-xl lg:text-3xl">
            {{ data.work.overview }}
          </div>
        </div>
        <DatoStructuredText :data="data.work.body" class="editor-full" />
        <div class="mx-auto max-w-editor px-4 lg:px-10">
          <CollectionPager class="mt-12 lg:mt-20" :prev="pager.prev" :next="pager.next" />
        </div>
      </div>
    </article>
    <SectionDetailNext v-if="data.work" :current-record-slug="data.work.slug" />
  </div>
</template>
